import type { Branch } from "@domain/CRM/branch";
import type { Deal } from "@domain/CRM/deal";
import { classifySamplingItem, type ProagricaTypes } from "@domain/Integrations/Proagrica";
import { Job } from "@domain/CRM/job";
import type { User } from "@domain/Auth";
import type { IProagricaApi } from "@ports/integrations/IProagricaApi";
import type { IProagricaGeoFetcher } from "@ports/integrations/IProagricaGeoFetcher";

export interface ProagricaUseCasesDeps {
	proagricaApi: IProagricaApi;
	geoFetcher: IProagricaGeoFetcher;
	/** UUID generator for job.eventId. Defaults to a `crypto.randomUUID()`-based
	 *  generator when available; tests inject a deterministic counter. */
	generateUUID?: () => string;
}

interface FieldsLoadResult {
	farms: ProagricaTypes.GrowerFarm[];
	fields: ProagricaTypes.Field[];
	/** Pre-computed (n−1) deselection subsets keyed by sorted comma-joined
	 *  grower-id lists. The vm folds these into its own farmFieldCache so a
	 *  single grower deselection becomes a hit. */
	subsets: Map<string, { farms: ProagricaTypes.GrowerFarm[]; fields: ProagricaTypes.Field[] }>;
}

interface BuildJobsInput {
	selectedGrowers: ProagricaTypes.Grower[];
	selectedFields: ProagricaTypes.Field[];
	farms: ProagricaTypes.GrowerFarm[];
	fields: ProagricaTypes.Field[];
	deal: Deal | null;
	branch: Branch | null;
	user: User | undefined;
	/** Per-field cache lookup; cache hits reuse the existing Job verbatim
	 *  and skip every per-field IO call below. */
	getFromCache?: (fieldId: string) => Job | undefined;
	/** Per-field cache write; called when a fresh Job is built. */
	setInCache?: (fieldId: string, job: Job) => void;
	/** Optional progress reporter — `total` is the number of cache-miss
	 *  fields, `current` advances as each Job finishes building. */
	onProgress?: (progress: { current: number; total: number; label?: string }) => void;
	/** Maps the selected-grower id → deal it should seed; the GUI builds
	 *  this once per render. Optional; falls back to the first arg `deal`. */
	growerDealMap?: Record<string, Deal | null>;
}

interface BuildJobsResult {
	jobs: Job[];
	samplingItems: ProagricaTypes.SamplingActivity[];
	featureStats: { zones: number; centroids: number; cores: number };
}

/**
 * Drives the Proagrica-import workflow. Mirror of `AgrianUseCases`'
 * shape: cache-agnostic fetch + dedup + subset-derivation in the use
 * case; UI state and Map-keyed caches stay in the vm.
 */
export interface ProagricaUseCases {
	login(returnUrl: string): Promise<string>;
	loadGrowers(forceRefresh?: boolean): Promise<ProagricaTypes.Grower[]>;
	loadFieldsForGrowers(
		selectedGrowers: ProagricaTypes.Grower[],
		forceRefresh?: boolean,
		onProgress?: (progress: { current: number; total: number; label?: string }) => void,
	): Promise<FieldsLoadResult>;
	buildJobsFromFields(input: BuildJobsInput): Promise<BuildJobsResult>;
}

const defaultUUID = (): string => {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
	return Math.random().toString(36).slice(2);
};

function login(deps: ProagricaUseCasesDeps, returnUrl: string): Promise<string> {
	return deps.proagricaApi.login(returnUrl);
}

async function loadGrowers(deps: ProagricaUseCasesDeps, forceRefresh?: boolean): Promise<ProagricaTypes.Grower[]> {
	await deps.proagricaApi.refreshToken();
	return deps.proagricaApi.getGrowers(forceRefresh);
}

async function loadFieldsForGrowers(
	deps: ProagricaUseCasesDeps,
	selectedGrowers: ProagricaTypes.Grower[],
	forceRefresh?: boolean,
	onProgress?: (progress: { current: number; total: number; label?: string }) => void,
): Promise<FieldsLoadResult> {
	if (selectedGrowers.length === 0) {
		return { farms: [], fields: [], subsets: new Map() };
	}

	await deps.proagricaApi.refreshToken();

	const growerIds = selectedGrowers.map((g) => g.ID);
	const farmsWithGrowers = await deps.proagricaApi.getFarmsForGrowers(
		growerIds,
		selectedGrowers[0]!.SyncID,
		forceRefresh,
	);
	const allFarms = farmsWithGrowers.flatMap(({ farms }) => farms);

	const total = selectedGrowers.length;
	let loaded = 0;
	const allFields: ProagricaTypes.Field[] = [];
	if (total > 1) onProgress?.({ current: 0, total, label: "Loading fields…" });

	for (const { farms, grower: growerId } of farmsWithGrowers) {
		const grower = selectedGrowers.find((g) => g.ID === growerId);
		if (!grower) continue;
		if (total > 1) onProgress?.({ current: loaded, total, label: `Loading fields for ${grower.Name}…` });
		const fields = await deps.proagricaApi.getFieldsForGrowerFarms(grower, farms, grower.SyncID, forceRefresh);
		allFields.push(...fields);
		loaded++;
	}

	const subsets = new Map<string, { farms: ProagricaTypes.GrowerFarm[]; fields: ProagricaTypes.Field[] }>();
	for (let i = 0; i < growerIds.length; i++) {
		const subsetIds = growerIds.filter((_, j) => j !== i);
		if (subsetIds.length > 0) {
			const subKey = [...subsetIds].sort().join(",");
			if (!subsets.has(subKey)) {
				const subsetIdSet = new Set(subsetIds);
				const subFarms = allFarms.filter((f) => subsetIdSet.has(f.ParentID));
				const farmIds = new Set(subFarms.map((sf) => sf.ID));
				const subFields = allFields.filter((f) => farmIds.has(f.FarmID));
				subsets.set(subKey, { farms: subFarms, fields: subFields });
			}
		}
	}

	return { farms: allFarms, fields: allFields, subsets };
}

async function buildJobsFromFields(deps: ProagricaUseCasesDeps, input: BuildJobsInput): Promise<BuildJobsResult> {
	const generateUUID = deps.generateUUID ?? defaultUUID;

	const selectedFieldIds = new Set(input.selectedFields.map((f) => f.ID));
	const missingFieldIds = [...selectedFieldIds].filter((id) => !input.getFromCache?.(id));
	if (missingFieldIds.length > 0) {
		await deps.proagricaApi.refreshToken();
	}

	const accumulatedSamplingItems: ProagricaTypes.SamplingActivity[] = [];
	const newJobs: Job[] = [];
	const total = missingFieldIds.length;
	let loaded = 0;
	if (total > 0) input.onProgress?.({ current: 0, total, label: "Loading sampling events…" });

	for (const grower of input.selectedGrowers) {
		const deal = input.growerDealMap?.[grower.ID] ?? input.deal;
		const growerFarms = input.farms.filter((farm) => farm.ParentID === grower.ID);

		for (const growerFarm of growerFarms) {
			const growerFields = input.fields.filter((field) => field.FarmID === growerFarm.ID);
			const selectedGrowerFields = growerFields.filter((f) => selectedFieldIds.has(f.ID));
			if (selectedGrowerFields.length === 0) continue;

			const needsFarm = selectedGrowerFields.some((f) => !input.getFromCache?.(f.ID));
			const farm = needsFarm ? await deps.proagricaApi.getFarm(growerFarm.ID, grower.SyncID) : null;

			for (const field of selectedGrowerFields) {
				const cached = input.getFromCache?.(field.ID);
				if (cached) {
					newJobs.push(cached);
					continue;
				}

				input.onProgress?.({ current: loaded, total, label: `${grower.Name} — ${field.Name ?? ""}` });

				const items = await deps.proagricaApi.getSamplingItems(field.ID, field.SyncID);
				accumulatedSamplingItems.push(...items);

				const fieldSamplingItems = accumulatedSamplingItems
					.filter((item) => item.FieldID === field.ID)
					.sort((a, b) => (b["ModifiedOn"] as string).localeCompare(a["ModifiedOn"] as string));

				const bndGeoJSON = deps.geoFetcher.getBoundaryGeoJSON(field.CurrentBoundary.Records);
				let znsGeoJSON: import("geojson").FeatureCollection | undefined;
				let ptsGeoJSON: import("geojson").FeatureCollection | undefined;
				const kind = classifySamplingItem(fieldSamplingItems[0]);
				if (kind !== "none") {
					const geoJSON = deps.geoFetcher.getZoneOrPointsGeoJSON(fieldSamplingItems[0]!);
					if (kind === "zone") znsGeoJSON = geoJSON;
					else ptsGeoJSON = geoJSON;
				}

				const newJob: Job = Job.defaultForImport({
					fieldName: field.Name,
					depth: deal?.depth,
					density: deal?.density,
					growerName: grower.Name,
					frequencyOfMicros: deal?.frequencyOfMicros,
					testPackage: deal?.testPackage,
					farmName: farm?.Name ?? "",
				});
				newJob.files = { pointsGeoJSON: ptsGeoJSON, boundaryGeoJSON: bndGeoJSON, zonesGeoJSON: znsGeoJSON };
				newJob.dealIds = deal?.id ? [deal.id] : [];
				newJob.fieldId = field.ID;
				newJob.fieldPriority = "Regular Turn (default)";
				newJob.submitterEmail = input.user?.email;
				newJob.submitterName = input.user?.name;
				newJob.branchIds = input.branch ? [input.branch.id] : [];
				newJob.externalGrower = { id: grower.ID, name: grower.Name };
				newJob.eventId = generateUUID();

				input.setInCache?.(field.ID, newJob);
				loaded++;
				input.onProgress?.({ current: loaded, total, label: `${grower.Name} — ${field.Name ?? ""}` });
				newJobs.push(newJob);
			}
		}
	}

	const featureStats = {
		zones: newJobs.reduce((sum, j) => sum + (j.files.zonesGeoJSON?.features?.length || 0), 0),
		centroids: newJobs.reduce(
			(sum, j) => sum + (j.files.zonesGeoJSON?.features?.length ? 0 : j.files.pointsGeoJSON?.features?.length || 0),
			0,
		),
		cores: newJobs.reduce(
			(sum, j) => sum + (j.files.zonesGeoJSON?.features?.length ? j.files.pointsGeoJSON?.features?.length || 0 : 0),
			0,
		),
	};

	return { jobs: newJobs, samplingItems: accumulatedSamplingItems, featureStats };
}

export function makeProagricaUseCases(deps: ProagricaUseCasesDeps): ProagricaUseCases {
	return {
		login: (returnUrl) => login(deps, returnUrl),
		loadGrowers: (forceRefresh) => loadGrowers(deps, forceRefresh),
		loadFieldsForGrowers: (selectedGrowers, forceRefresh, onProgress) =>
			loadFieldsForGrowers(deps, selectedGrowers, forceRefresh, onProgress),
		buildJobsFromFields: (input) => buildJobsFromFields(deps, input),
	};
}
